import { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Button } from 'components/atoms/Button';
import { useTextInput } from 'components/atoms/Input';
import { InputLabel } from 'components/atoms/InputLabel';
import { ErrorMessage } from 'components/molecules/ErrorMessage';
import { ButtonModal } from 'components/organisms/ButtonModal';

import { useAuth } from 'libs/AuthContext';
import { media } from 'utils';

import * as fonts from 'styles/fonts';
import * as mixins from 'styles/mixins';

type PasswordType = 'password' | 'text';

function ChangePasswordForm() {
  const history = useHistory();
  const { changePasswordSuccess, error, patchChangePassword, resetInitailizeStatus } = useAuth();

  const [showButtonModal, setShowButtonModal] = useState(false);
  const [coincide, setCoincide] = useState(false);
  const [passwordType, setPasswordType] = useState<PasswordType>('password');
  const [changePasswordType, setChangePasswordType] = useState<PasswordType>('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState<PasswordType>('password');

  const [password, passwordInput] = useTextInput({
    id: 'passwordInput',
    placeholder: '현재 비밀번호를 입력해주세요.',
    type: passwordType,
  });

  const [changePassword, changePasswordInput] = useTextInput({
    id: 'changePasswordInput',
    placeholder: '숫자 영문자 조합 8자 이상',
    type: changePasswordType,
  });

  const [confirmPassword, confirmPasswordInput] = useTextInput({
    id: 'confirmPasswordInput',
    placeholder: '비밀번호를 다시 입력해주세요.',
    type: confirmPasswordType,
  });

  const changePasswordBtnEnabled = useMemo(
    () => !!password && !!changePassword && !!confirmPassword,
    [password, changePassword, confirmPassword],
  );

  const closeButtonModal = () => {
    setShowButtonModal(false);
    resetInitailizeStatus();

    if (changePasswordSuccess) return history.push('/guardian');
  };

  const confirmMessage = useMemo(() => {
    if (changePasswordSuccess) {
      return <ModalText>비밀번호가 성공적으로 변경되었습니다.</ModalText>;
    }

    if (error) {
      return <ModalText>사용자 정보를 다시 확인해주세요.</ModalText>;
    }
  }, [changePasswordSuccess, error]);

  const handlePasswordType = () => {
    setPasswordType((prev) => (prev === 'password' ? 'text' : 'password'));
  };

  const handleChangePasswordType = () => {
    setChangePasswordType((prev) => (prev === 'password' ? 'text' : 'password'));
  };

  const handleConfirmPasswordType = () => {
    setConfirmPasswordType((prev) => (prev === 'password' ? 'text' : 'password'));
  };

  const handleGoBack = () => {
    history.push('/guardian');
  };

  const handleChangePasswordBtnClick = () => {
    if (changePassword !== confirmPassword && !!confirmPassword) return setCoincide(true);

    patchChangePassword(password, changePassword);
  };

  useEffect(() => {
    if (changePassword && confirmPassword) return setCoincide(false);
  }, [changePassword, confirmPassword]);

  useEffect(() => {
    if (changePasswordSuccess || error) return setShowButtonModal(true);
  }, [changePasswordSuccess, error]);

  return (
    <>
      <ButtonModal
        buttons={[{ type: 'close', name: '확인', action: () => closeButtonModal() }]}
        isOpen={showButtonModal}
      >
        {confirmMessage}
      </ButtonModal>
      <FormContainer>
        <div>
          <Header>
            <BackArrowIcon onClick={() => handleGoBack()} />
            <MainText>비밀번호 변경</MainText>
          </Header>
          <InputWrap>
            <InputLabel htmlFor="passwordInput">현재 비밀번호</InputLabel>
            <PwToogleBtnWrapper>
              <PwToogleBtn type={passwordType} onClick={() => handlePasswordType()} />
              {passwordInput}
            </PwToogleBtnWrapper>
          </InputWrap>
          <InputWrap>
            <InputLabel htmlFor="changePasswordInput">변경할 비밀번호</InputLabel>
            <PwToogleBtnWrapper>
              <PwToogleBtn type={changePasswordType} onClick={() => handleChangePasswordType()} />
              {changePasswordInput}
            </PwToogleBtnWrapper>
          </InputWrap>
          <InputWrap error={coincide}>
            <InputLabel htmlFor="confirmPasswordInput">비밀번호 확인</InputLabel>
            <PwToogleBtnWrapper>
              <PwToogleBtn type={confirmPasswordType} onClick={() => handleConfirmPasswordType()} />
              {confirmPasswordInput}
            </PwToogleBtnWrapper>
          </InputWrap>
          {coincide && <ErrorMessage>비밀번호가 일치하지 않습니다.</ErrorMessage>}
        </div>
        <ChangePasswordBtn
          primary
          disabled={!changePasswordBtnEnabled}
          size="mid"
          onClick={() => handleChangePasswordBtnClick()}
        >
          비밀번호 변경
        </ChangePasswordBtn>
      </FormContainer>
    </>
  );
}

export default ChangePasswordForm;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  box-sizing: border-box;
  background-color: var(--white);

  ${media.main} {
    max-width: 675px;
    max-height: 1112px;
  }

  input {
    flex: 1;
    &:focus {
      font-size: 14px;
    }
  }
`;

const Header = styled.header`
  ${mixins.flexCenter}
  position: relative;
  margin-bottom: 52px;
`;

const BackArrowIcon = styled.div`
  position: absolute;
  left: 0px;
  width: 32px;
  height: 32px;
  background-image: url('/icons/ic-back-arrow.svg');
  cursor: pointer;
`;

const MainText = styled.h1`
  font-family: NotoSansMedium;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 27px;
  color: var(--gray-600);
  margin: 0;
`;

const InputWrap = styled.div<{ error?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 26px;

  ${({ error }) =>
    error &&
    css`
      margin-bottom: 8px;
    `}

  input {
    flex: 1;
  }
`;

const Disabled = css`
  color: var(--gray-400);
  background-color: var(--gray-80);
  border: 1px solid var(--gray-150);
`;

const ChangePasswordBtn = styled(Button)<{ disabled: boolean }>`
  ${fonts.normal};
  ${fonts.demi};
  color: var(--white);
  font-size: 20px;
  width: 100%;
  height: 60px;
  ${({ disabled }) => disabled && Disabled}
`;

const ModalText = styled.span`
  color: #777777;
  font-family: NotoSansRegular;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  white-space: pre-wrap;
`;

const PwToogleBtnWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const PwToogleBtn = styled.div<{ type: string }>`
  position: absolute;
  right: 8px;
  width: 32px;
  height: 32px;
  background-image: url('/icons/ic-password-show.svg');
  background-repeat: no-repeat;
  background-position: center center;

  ${({ type }) =>
    type === 'text' &&
    css`
      background-image: url('/icons/ic-password-hide.svg');
    `}
`;
